import ProtectedRouter from "@/context/auth/ProtectedRouter";
import { TenantProvider } from "@/context/TenantContext";
import MainLayout from "@/layouts/MainLayout";
import StarterLayout from "@/layouts/StarterLayout";
import NotFoundPage from "@/pages/NotFoundPage";
import routes from "@/utils/routes";
import propertyPortalRoutes from "@/project/propertyPortal/utils/routes";
import { PropertyPortalProviders } from "@/project/propertyPortal/app";
import SchemasListPage from "@/pages/SchemaManagement/SchemasListPage";
import propertyPortalNav from "@/project/propertyPortal/utils/navigationRoutes";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
} from "react-router-dom";

/** When false (default), `/` sends logged-in users to property portal dashboard. Set `true` to keep Architecture One schemas at `/`. */
const useSchemasAsHome = import.meta.env.VITE_SCHEMAS_AS_HOME === "true";

function App() {
  return (
    <TenantProvider>
      <AppWithRouter />
    </TenantProvider>
  );
}

const AppWithRouter = () => {
  const mainPublicRoutes = routes.find(m => m.type === "public")?.routes ?? [];
  const mainAuthRoutes = routes.find(m => m.type === "auth")?.routes ?? [];
  const propertyPortalAuthRoutes =
    propertyPortalRoutes.find(m => m.type === "auth")?.routes ?? [];
  const propertyPortalPublicRoutes =
    propertyPortalRoutes.find(m => m.type === "public")?.routes ?? [];

  const publicRoutes = [...propertyPortalPublicRoutes, ...mainPublicRoutes];
  const authRoutes = mainAuthRoutes;
  const propertyPortalRoutesOnly = propertyPortalAuthRoutes.filter(
    r => r.path !== "*"
  );

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<TopLevelComponent />}>
          {/* ================= PUBLIC ROUTES ================= */}
          {publicRoutes.map(({ path, element: Page }) => (
            <Route key={path} path={path} element={<StarterLayout />}>
              <Route index element={<Page />} />
            </Route>
          ))}

          {/* ================= PROPERTY PORTAL (requires login) ================= */}
          <Route element={<ProtectedRouter />}>
            <Route element={<PropertyPortalProviders />}>
              {propertyPortalRoutesOnly.map(({ path, element: Page }) => (
                <Route
                  key={path}
                  path={path.replace(/^\//, "")}
                  element={<Page />}
                />
              ))}
            </Route>
          </Route>

          {/* ================= PROTECTED ROUTES (main app) ================= */}
          <Route element={<ProtectedRouter />}>
            <Route element={<MainLayout />}>
              <Route
                index
                element={
                  useSchemasAsHome ? (
                    <SchemasListPage />
                  ) : (
                    <Navigate to={propertyPortalNav.DASHBOARD} replace />
                  )
                }
              />
              {authRoutes.map(({ path, element: Page }) => (
                <Route key={path} path={path} element={<Page />} />
              ))}
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

const TopLevelComponent = () => {
  return <Outlet />;
};

export default App;
